import React, { useState, useRef, createElement } from 'react';
import { Payment } from '../../types';
import { Button } from '../../components/ui/Button';
import { Printer, Download, X } from 'lucide-react';
import { useData } from '../../store/DataContext';
import { formatCurrency } from './utils';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
interface Props {
  payment: Payment;
  onClose: () => void;
}
async function captureToA4Pdf(element: HTMLElement, filename: string) {
  const PAGE_W_MM = 210;
  const PAGE_H_MM = 297;
  const MARGIN_TOP_MM = 20;
  const MARGIN_BOTTOM_MM = 20;
  const MARGIN_LEFT_MM = 18;
  const MARGIN_RIGHT_MM = 18;
  const CONTENT_W_MM = PAGE_W_MM - MARGIN_LEFT_MM - MARGIN_RIGHT_MM;
  const CONTENT_H_MM = PAGE_H_MM - MARGIN_TOP_MM - MARGIN_BOTTOM_MM;
  const sourceCanvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false
  });
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true
  });
  const sourceW = sourceCanvas.width;
  const sourceH = sourceCanvas.height;
  const pxPerMm = sourceW / CONTENT_W_MM;
  const pageSlicePx = Math.floor(CONTENT_H_MM * pxPerMm);
  let cursorPx = 0;
  let pageIndex = 0;
  while (cursorPx < sourceH) {
    const remainingPx = sourceH - cursorPx;
    const sliceHpx = Math.min(pageSlicePx, remainingPx);
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = sourceW;
    sliceCanvas.height = sliceHpx;
    const ctx = sliceCanvas.getContext('2d');
    if (!ctx) break;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, sourceW, sliceHpx);
    ctx.drawImage(sourceCanvas, 0, cursorPx, sourceW, sliceHpx, 0, 0, sourceW, sliceHpx);
    const sliceImg = sliceCanvas.toDataURL('image/jpeg', 0.95);
    const sliceHmm = sliceHpx / pxPerMm;
    if (pageIndex > 0) pdf.addPage();
    pdf.addImage(sliceImg, 'JPEG', MARGIN_LEFT_MM, MARGIN_TOP_MM, CONTENT_W_MM, sliceHmm);
    cursorPx += sliceHpx;
    pageIndex += 1;
  }
  pdf.save(filename);
}
export const ReceiptModal: React.FC<Props> = ({
  payment,
  onClose
}) => {
  const {
    patients,
    walkInClients,
    invoices,
    users
  } = useData();
  const printableRef = useRef<HTMLDivElement>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const getCustomer = () => {
    if (payment.customerType === 'Patient') {
      return patients.find(p => p.id === payment.customerId);
    } else {
      return walkInClients.find(c => c.id === payment.customerId);
    }
  };
  const customer = getCustomer();
  const invoice = invoices.find(i => i.id === payment.invoiceId);
  const cashier = users.find(u => u.id === payment.cashier);
  const handlePrint = () => {
    document.body.classList.add('printing-test-modal');
    window.print();
    setTimeout(() => {
      document.body.classList.remove('printing-test-modal');
    }, 500);
  };
  const handleDownloadPdf = async () => {
    if (!printableRef.current) return;
    setIsGeneratingPdf(true);
    try {
      const filename = `BMED_${payment.receiptNumber}.pdf`;
      await captureToA4Pdf(printableRef.current, filename);
    } catch (err) {
      console.error('PDF generation failed', err);
      alert('Failed to generate PDF.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };
  return <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 print:p-0 print:bg-white" data-id="element-1423">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[95vh] overflow-y-auto print:max-h-none print:max-w-none print:shadow-none print:rounded-none print:overflow-visible test-modal-print" data-id="element-1424">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-3 flex justify-between items-center z-10 print:hidden" data-id="element-1425">
          <h3 className="text-lg font-semibold text-gray-900" data-id="element-1426">
            Receipt {payment.receiptNumber}
          </h3>
          <div className="flex items-center space-x-2" data-id="element-1427">
            <Button variant="outline" size="sm" onClick={handlePrint} data-id="element-1428">
              <Printer className="w-4 h-4 mr-1" data-id="element-1429" /> Print
            </Button>
            <Button variant="primary" size="sm" onClick={handleDownloadPdf} disabled={isGeneratingPdf} data-id="element-1430">
              <Download className="w-4 h-4 mr-1" data-id="element-1431" />
              {isGeneratingPdf ? 'Generating...' : 'Download PDF'}
            </Button>
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded" aria-label="Close" data-id="element-1432">
              <X className="w-5 h-5 text-gray-500" data-id="element-1433" />
            </button>
          </div>
        </div>

        <div ref={printableRef} className="p-8 bg-white text-sm font-sans text-black" data-id="element-1434">
          <div className="text-center mb-8 border-b-2 border-black pb-6" data-id="element-1435">
            <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED" className="h-20 w-20 object-contain mx-auto mb-2" data-id="element-1436" />
            <h1 className="text-2xl font-bold uppercase text-pink-600" data-id="element-1437">
              BMED Medical Centre
            </h1>
            <p className="text-sm italic text-gray-600" data-id="element-1438">
              Maternity Care Made Better
            </p>
            <h2 className="text-xl font-bold uppercase tracking-widest mt-4 text-gray-800" data-id="element-1439">
              OFFICIAL RECEIPT
            </h2>
          </div>

          <div className="grid grid-cols-2 gap-6 mb-8" data-id="element-1440">
            <div data-id="element-1441">
              <p className="mb-1" data-id="element-1442">
                <span className="font-semibold" data-id="element-1443">Receipt #:</span>{' '}
                {payment.receiptNumber}
              </p>
              <p className="mb-1" data-id="element-1444">
                <span className="font-semibold" data-id="element-1445">Date:</span>{' '}
                {new Date(payment.date).toLocaleString()}
              </p>
              <p className="mb-1" data-id="element-1446">
                <span className="font-semibold" data-id="element-1447">Invoice #:</span>{' '}
                {invoice?.invoiceNumber || '—'}
              </p>
              <p className="mb-1" data-id="element-1448">
                <span className="font-semibold" data-id="element-1449">Cashier:</span>{' '}
                {cashier?.fullName || '—'}
              </p>
            </div>
            <div className="text-right" data-id="element-1450">
              <p className="font-bold text-lg mb-1" data-id="element-1451">Received From:</p>
              {payment.customerType === 'Patient' ? <>
                  <p data-id="element-1452">
                    {(customer as any)?.firstName} {(customer as any)?.lastName}
                  </p>
                  <p data-id="element-1453">File No: {(customer as any)?.bmcFileNumber}</p>
                </> : <p data-id="element-1454">{(customer as any)?.name}</p>}
            </div>
          </div>

          <div className="border-t-2 border-b-2 border-gray-300 py-4 mb-8" data-id="element-1455">
            <div className="flex justify-between items-center mb-2" data-id="element-1456">
              <span className="font-semibold text-lg" data-id="element-1457">Payment Method:</span>
              <span className="text-lg" data-id="element-1458">{payment.method}</span>
            </div>
            <div className="flex justify-between items-center" data-id="element-1459">
              <span className="font-bold text-xl uppercase" data-id="element-1460">Amount Paid:</span>
              <span className="font-bold text-2xl text-green-600" data-id="element-1461">
                {formatCurrency(payment.amount)}
              </span>
            </div>
          </div>

          {invoice && <div className="bg-gray-50 p-4 rounded-lg mb-8" data-id="element-1462">
              <h4 className="font-bold uppercase text-gray-600 mb-2" data-id="element-1463">
                Invoice Summary
              </h4>
              <div className="flex justify-between py-1" data-id="element-1464">
                <span className="text-gray-600" data-id="element-1465">Total Invoice Amount:</span>
                <span className="font-medium" data-id="element-1466">
                  {formatCurrency(invoice.totalAmount)}
                </span>
              </div>
              <div className="flex justify-between py-1" data-id="element-1467">
                <span className="text-gray-600" data-id="element-1468">Total Paid to Date:</span>
                <span className="font-medium text-green-600" data-id="element-1469">
                  {formatCurrency(invoice.amountPaid)}
                </span>
              </div>
              <div className="flex justify-between py-1 border-t border-gray-300 mt-1 pt-1" data-id="element-1470">
                <span className="font-bold text-gray-800" data-id="element-1471">
                  Remaining Balance:
                </span>
                <span className="font-bold text-red-600" data-id="element-1472">
                  {formatCurrency(invoice.balanceDue)}
                </span>
              </div>
            </div>}

          {payment.notes && <div className="mb-8" data-id="element-1473">
              <h4 className="font-bold uppercase text-gray-600 mb-1" data-id="element-1474">Notes</h4>
              <p className="text-gray-700" data-id="element-1475">{payment.notes}</p>
            </div>}

          <div className="flex justify-between items-end mt-12 pt-8 border-t border-gray-200" data-id="element-1476">
            <div className="text-center" data-id="element-1477">
              <img src={`https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${payment.receiptNumber}`} alt="QR Code" className="w-24 h-24 mx-auto mb-2" data-id="element-1478" />
              <p className="text-xs text-gray-500" data-id="element-1479">Scan to verify</p>
            </div>
            <div className="text-center" data-id="element-1480">
              <p className="text-sm italic text-gray-600 mb-4" data-id="element-1481">
                Thank you for your business!
              </p>
              <div className="border-t border-black w-48 mx-auto pt-1" data-id="element-1482">
                <p className="text-xs font-bold uppercase" data-id="element-1483">
                  Authorized Signature
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};